/**
 * girlcave playlist player — drives the designed transport (Figma 31:4439)
 * through Spotify's official IFrame API.
 *
 * Re-introduced 2026-08-30 (user request: "map the Spotify embed on the
 * custom controls"). The embed iframe is mounted hidden; only the designed
 * chrome pills are visible.
 *
 * SHUFFLE (user request 2026-08-30: "the playlist should be always played
 * in shuffle when started"). Spotify's embed has NO shuffle command — its
 * internal toggle_shuffle is auth-restricted — and its track list is
 * unreachable from the browser (the embed page sends no CORS headers and
 * the anonymous token endpoint is blocked from third-party origins, both
 * verified 2026-08-30). The playlist's tracks are therefore captured
 * SERVER-SIDE (the embed page's __NEXT_DATA__) and embedded in the snippet
 * as data-gc-player-shuffle="<playlistId>|<uri,uri,…>". With a matching
 * list the player runs its own shuffled queue — decided at mount, with the
 * FIRST random track as the controller's initial entity (so the first ▶ is
 * a plain togglePlay() inside the click gesture):
 *   - loadUri(track) swaps the iframe src — commands sent right after are
 *     LOST, so play() waits for the fresh embed's one-shot 'ready' event.
 *   - next / previous move through the shuffled order (real back button);
 *     previous restarts the current track first when >3s in, like Spotify.
 *   - track end → auto-advance (single-track context has no queue of its
 *     own): advanced when position closes to within ~1.3s of duration, or
 *     when the embed stalls paused at the end between the ~1/s updates.
 * Without a matching list (playlist changed, list not yet refreshed) the
 * player falls back to the embed's own in-order queue: toggle →
 * togglePlay(), next → seek(duration − 1.2s) so the playlist queue advances,
 * previous → seek(0) restart. Refresh path documented in AGENTS.md §10.
 *
 * CLICK-TO-LOAD (go-live audit 2026-09-24, user direction: "load spotify
 * after click on play or skip buttons"). Nothing talks to Spotify before a
 * transport press — no IFrame API script, no embed iframe, no oEmbed fetch:
 * the embed sets Spotify cookies and every request hands Spotify the
 * visitor's IP, which § 25 TDDDG / GDPR do not allow before consent. Until
 * then the designed card (mock cover + title) is all there is. The first
 * press on ANY of the three pills loads the API, mounts THAT instance's
 * embed and starts playback of the first shuffled track — ⏮/⏭ included,
 * there is no current track to skip from yet. Later presses run as before.
 *
 * The snippet renders twice (sidebar ≥1080px, mobile slot <1080px); only
 * the pressed instance ever mounts. Playing one instance pauses the other,
 * so a resize can never leave two playlists talking at once.
 */
(function () {
  var API_SRC = 'https://open.spotify.com/embed/iframe-api/v1';
  /* The embed's playback_update carries duration/position in MILLISECONDS;
     seek() takes SECONDS (the embed parses `timestamp` and multiplies by
     1000). Fallback next: 1.2s before the end is deep enough that the
     playlist queue's advance is the only sensible outcome. */
  var NEXT_SEEK_MARGIN_MS = 1200;
  /* Queue auto-advance: close the last ~1.3s gaplessly; the paused-at-end
     net catches the embed stopping between the ~1/s updates. */
  var ADVANCE_MARGIN_MS = 1300;
  var ADVANCE_STALLED_MS = 1500;
  /* previous(): restart the current track instead of stepping back when
     more than 3s have played (standard player semantics). */
  var PREV_RESTART_MS = 3000;
  /* Consecutive load errors before the shuffled queue gives up and the
     player degrades to the embed's in-order fallback. */
  var MAX_ERRORS = 3;

  /* Live card metadata (user request 2026-08-30: dynamic cover, title and
     currently playing track). Spotify's oEmbed endpoint is CORS-open
     (access-control-allow-origin: *, verified 2026-08-30) and returns
     { title, thumbnail_url } for playlist AND track URIs — the embed page
     itself stays CORS-blocked, so this is the only anonymous source for
     names. Designed mock values remain as the no-JS / fetch-failure
     fallback; text goes in via textContent only. */
  var OEMBED_ENDPOINT = 'https://open.spotify.com/oembed?url=';
  var metaCache = Object.create(null);
  var metaPending = Object.create(null);

  function fetchMeta(uri, done) {
    if (uri in metaCache) {
      done(metaCache[uri]);
      return;
    }
    if (metaPending[uri]) {
      metaPending[uri].push(done);
      return;
    }
    metaPending[uri] = [done];
    fetch(OEMBED_ENDPOINT + encodeURIComponent(uri))
      .then(function (response) {
        if (!response.ok) throw new Error('oembed ' + response.status);
        return response.json();
      })
      .then(function (data) {
        metaCache[uri] = data;
        metaPending[uri].forEach(function (cb) {
          cb(data);
        });
        delete metaPending[uri];
      })
      .catch(function () {
        metaCache[uri] = null; /* no retry storm; the mock stays */
        metaPending[uri].forEach(function (cb) {
          cb(null);
        });
        delete metaPending[uri];
      });
  }

  function applyPlaylistMeta(player) {
    fetchMeta(player.playlistUri, function (meta) {
      if (!meta || !player.root.isConnected) return;
      var cover = player.root.querySelector('.gc-player__cover');
      var title = player.root.querySelector('.gc-player__title');
      if (cover && meta.thumbnail_url) cover.src = meta.thumbnail_url;
      if (title && meta.title) title.textContent = meta.title;
    });
  }

  /* --- shuffled queue --------------------------------------------------- */

  function parseShuffle(root) {
    var raw = root.getAttribute('data-gc-player-shuffle');
    if (!raw) return null;
    var sep = raw.indexOf('|');
    if (sep === -1) return null;
    var tracks = raw.slice(sep + 1).split(',').filter(function (uri) {
      return uri.indexOf('spotify:') === 0;
    });
    return tracks.length > 1 ? tracks : null;
  }

  function shuffled(list) {
    var out = list.slice();
    for (var i = out.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = out[i];
      out[i] = out[j];
      out[j] = swap;
    }
    return out;
  }

  /* Load queue[index] and — when wantPlay — start it. The 'ready' one-shot
     is mandatory: loadUri of a track replaces the iframe src, and commands
     posted before the new document signals ready are lost. */
  function playIndex(player, index, wantPlay) {
    var uri = player.queue[index];
    if (player.pendingReady) {
      player.controller.removeListener('ready', player.pendingReady);
      player.pendingReady = null;
    }
    player.queueIndex = index;
    player.advancing = true;
    if (uri === player.playingUri) {
      player.advancing = false;
      if (wantPlay) player.controller.play();
      return;
    }
    player.pendingReady = function () {
      player.pendingReady = null;
      if (wantPlay) player.controller.play();
    };
    player.controller.addListener('ready', player.pendingReady);
    player.controller.loadUri(uri);
  }

  function startQueue(root) {
    var tracks = parseShuffle(root);
    return tracks ? shuffled(tracks) : null;
  }

  /* Track changes must stay out of the browser history. loadUri() — every
     shuffled track change — assigns iframeElement.src, and a src change on a
     loaded iframe is a navigation that PUSHES a session-history entry
     (2026-09-24: history.length grew by one per track). Back then stepped
     the hidden embed back a track instead of leaving the page, far more
     visible since page changes no longer reload (assets/gc-navigation.js).
     A cross-origin frame may still be navigated with location.replace(),
     which loads the same URL without an entry, so the SDK's src writes are
     routed there. The SDK never reads src back (checked against the
     bundle 2026-09-24). */
  function keepOutOfHistory(iframe) {
    var native = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'src');
    if (!iframe || !native || !native.set) return;
    var current = null;
    Object.defineProperty(iframe, 'src', {
      configurable: true,
      get: function () {
        return current || native.get.call(iframe);
      },
      set: function (value) {
        var frame = iframe.contentWindow;
        if (!frame) {
          native.set.call(iframe, value);
          return;
        }
        current = String(value);
        try {
          frame.location.replace(current);
        } catch (error) {
          native.set.call(iframe, value);
        }
      },
    });
  }

  /* --- transport --------------------------------------------------------- */

  function syncToggle(player) {
    if (!player.toggleBtn) return;
    var label = player.playing
      ? player.toggleBtn.getAttribute('data-label-pause')
      : player.toggleBtn.getAttribute('data-label-play');
    if (label) player.toggleBtn.setAttribute('aria-label', label);
  }

  function setPlaying(player, playing) {
    if (player.playing === playing) return;
    player.playing = playing;
    player.root.classList.toggle('is-playing', playing);
    syncToggle(player);
    if (playing) {
      players.forEach(function (other) {
        if (other !== player && other.playing) {
          other.playing = false;
          other.root.classList.remove('is-playing');
          syncToggle(other);
          try {
            other.controller.pause();
          } catch (error) {
            /* the other embed may already be gone */
          }
        }
      });
    }
  }

  function onPlaybackUpdate(player, data) {
    if (!data) return;
    setLoading(player.root, false);
    player.duration = typeof data.duration === 'number' ? data.duration : 0;
    player.position = typeof data.position === 'number' ? data.position : 0;
    setPlaying(player, data.isPaused === false);
    if (data.playingURI && data.playingURI !== player.playingUri) {
      /* The new track reported in — the transition is over. */
      player.playingUri = data.playingURI;
      player.advancing = false;
      player.errorCount = 0;
      fetchMeta(data.playingURI, function (meta) {
        var el = player.root.querySelector('[data-gc-player-track]');
        if (el && meta && meta.title) {
          el.textContent = meta.title;
          el.hidden = false;
        }
      });
    }
    /* Track end → next shuffled track. */
    if (
      player.queue &&
      !player.advancing &&
      player.duration > 0 &&
      ((data.isPaused === false &&
        player.position + ADVANCE_MARGIN_MS >= player.duration) ||
        (data.isPaused === true &&
          !player.userPaused &&
          player.position + ADVANCE_STALLED_MS >= player.duration))
    ) {
      playIndex(player, (player.queueIndex + 1) % player.queue.length, true);
    }
  }

  function runAction(player, action) {
    try {
      if (action === 'toggle') {
        player.userPaused = player.playing;
        player.controller.togglePlay();
      } else if (action === 'next') {
        if (player.queue) {
          playIndex(
            player,
            (player.queueIndex + 1) % player.queue.length,
            player.playing
          );
          return;
        }
        if (player.duration > 0) {
          player.controller.seek(
            Math.max((player.duration - NEXT_SEEK_MARGIN_MS) / 1000, 0)
          );
        }
      } else if (action === 'prev') {
        if (player.queue) {
          /* Standard semantics: restart the current track when it is
             already under way, otherwise the real previous one. */
          if (player.position > PREV_RESTART_MS) {
            player.controller.seek(0);
            return;
          }
          if (player.queueIndex > 0) {
            playIndex(player, player.queueIndex - 1, player.playing);
            return;
          }
          player.controller.seek(0);
          return;
        }
        player.controller.seek(0);
      }
    } catch (error) {
      /* commands sent before the embed finished loading are queued by the
         SDK itself; a failing call here means the embed is gone */
    }
  }

  /* --- click-to-load ----------------------------------------------------- */

  var roots = Array.prototype.filter.call(
    document.querySelectorAll('.gc-player'),
    function (root) {
      return root.querySelector('[data-gc-player-uri]');
    }
  );
  if (!roots.length) return;

  var api = null;
  var apiRequested = false;
  var pending = [];
  var players = [];
  /* Give up on the busy state if the embed never reports in (blocked by a
     content blocker, offline) so the pills do not stay in "progress". */
  var LOADING_TIMEOUT_MS = 10000;

  function setLoading(root, loading) {
    root.classList.toggle('is-loading', loading);
    if (loading) {
      root.setAttribute('aria-busy', 'true');
    } else {
      root.removeAttribute('aria-busy');
    }
  }

  function playerFor(root) {
    for (var i = 0; i < players.length; i++) {
      if (players[i].root === root) return players[i];
    }
    return null;
  }

  function requestApi() {
    if (apiRequested) return;
    apiRequested = true;
    var script = document.createElement('script');
    script.src = API_SRC;
    script.async = true;
    script.onerror = function () {
      /* Blocked or offline: release the waiting cards so a later press
         tries again instead of hanging on a dead script tag. */
      apiRequested = false;
      script.remove();
      pending.splice(0).forEach(function (root) {
        setLoading(root, false);
        delete root.dataset.gcPlayerRequested;
      });
    };
    (document.head || document.documentElement).appendChild(script);
  }

  function mount(root) {
    if (!api) {
      pending.push(root);
      requestApi();
      return;
    }

    /* Resolve the host and URI NOW, while the placeholder is still attached:
       createController replaces it with the iframe synchronously (found
       2026-08-30 — resolving inside the async callback ran on a detached
       node). */
    var host = root.querySelector('[data-gc-player-uri]');
    if (!host || root.dataset.gcPlayerMounted) return;
    var playlistUri = host.getAttribute('data-gc-player-uri');
    /* Shuffled queue, decided at mount: the FIRST random track becomes the
       controller's initial entity, so the first ▶ press is a plain
       togglePlay() inside the click gesture — no async play() after a
       loadUri for the very first start. */
    var queue = startQueue(root);

    api.createController(
      host,
      { uri: queue ? queue[0] : playlistUri },
      function (controller) {
        if (root.dataset.gcPlayerMounted) return;
        root.dataset.gcPlayerMounted = 'true';
        keepOutOfHistory(controller.iframeElement);

        var player = {
          controller: controller,
          root: root,
          playlistUri: playlistUri,
          playingUri: '',
          toggleBtn: root.querySelector('[data-gc-action="toggle"]'),
          playing: false,
          duration: 0,
          position: 0,
          /* shuffled queue (null → embed's in-order fallback) */
          queue: queue,
          queueIndex: queue ? 0 : -1,
          advancing: false,
          userPaused: false,
          errorCount: 0,
          pendingReady: null,
        };

        applyPlaylistMeta(player);

        /* Release the busy state as soon as the embed reports in. */
        controller.addListener('ready', function () {
          setLoading(root, false);
        });

        controller.addListener('playback_update', function (event) {
          onPlaybackUpdate(player, event ? event.data : null);
        });

        controller.addListener('error', function () {
          if (!player.queue) return;
          if (player.pendingReady) {
            controller.removeListener('ready', player.pendingReady);
            player.pendingReady = null;
          }
          player.errorCount += 1;
          if (player.errorCount >= MAX_ERRORS) {
            player.queue = null; /* degrade to the in-order fallback */
            return;
          }
          playIndex(
            player,
            (player.queueIndex + 1) % player.queue.length,
            player.playing
          );
        });

        players.push(player);

        /* The SDK queues commands sent while the fresh embed loads
           (AGENTS.md §10), so play() can go out right away. */
        delete root.dataset.gcPlayerPendingAction;
        controller.play();
      }
    );
  }

  /* Assign the global BEFORE the bundle evals — it calls it either at once
     or through a defineProperty setter (AGENTS.md §10, trap 3). */
  window.onSpotifyIframeApiReady = function (IFrameAPI) {
    api = IFrameAPI;
    pending.splice(0).forEach(mount);
  };

  roots.forEach(function (root) {
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-gc-action]'),
      function (button) {
        button.addEventListener('click', function () {
          var action = button.getAttribute('data-gc-action');
          var player = playerFor(root);
          if (player) {
            runAction(player, action);
            return;
          }
          /* First press on this card — presses while it loads are dropped;
             the press that loaded the embed starts playback on mount. */
          if (root.dataset.gcPlayerRequested) return;
          root.dataset.gcPlayerRequested = 'true';
          setLoading(root, true);
          setTimeout(function () {
            setLoading(root, false);
          }, LOADING_TIMEOUT_MS);
          mount(root);
        });
      }
    );
  });
})();
